import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, FileText, Trash2, Search, Clock, Eye, FileDown } from 'lucide-react';
import { exportListPDF } from '@/utils/pdfExport';
import QuotationViewer from '@/components/QuotationViewer';
import CurrencySymbol from '@/components/CurrencySymbol';
import { SearchFilter } from '@/components/SearchFilter';
import { formatCurrency, fmtNum } from '@/utils/currency';

export default function Quotations() {
  const { token } = useContext(AuthContext);
  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    items: [],
    discount: 0,
    tax: 10,
    notes: '',
    customer_notes: '',
    currency: 'USD'
  });
  const [viewingQuotation, setViewingQuotation] = useState(null);
  const [showViewer, setShowViewer] = useState(false);

  useEffect(() => {
    fetchQuotations();
    fetchProducts();
    fetchCustomers();
    fetchExchangeRates();
  }, []);

  const fetchExchangeRates = async () => {
    try {
      const response = await axios.get(`${API}/settings/currency`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExchangeRates(response.data.exchange_rates || []);
    } catch (error) {
      console.error('Failed to fetch exchange rates');
    }
  };

  const fetchQuotations = async () => {
    try {
      const response = await axios.get(`${API}/quotations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort quotations: pending/sent first, then converted/expired
      const sorted = response.data.sort((a, b) => {
        const statusPriority = { 'pending': 1, 'sent': 2, 'converted': 3, 'expired': 4 };
        return (statusPriority[a.status] || 5) - (statusPriority[b.status] || 5);
      });
      
      setQuotations(sorted);
      setFilteredQuotations(sorted);
    } catch (error) {
      toast.error('Failed to load quotations');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers');
    }
  };

  const selectCustomer = (customer) => {
    setFormData({
      ...formData,
      customer_name: customer.name,
      customer_email: customer.email || '',
      customer_phone: customer.phone || ''
    });
  };

  const searchProducts = (query) => {
    setProductSearch(query);
    if (!query.trim()) {
      setFilteredProducts(products);
      return;
    }
    const filtered = products.filter(p =>
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      p.sku.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredProducts(filtered);
  };

  const addProductToQuotation = (product) => {
    const existingItem = formData.items.find(item => item.product_id === product.id);
    
    if (existingItem) {
      const newItems = formData.items.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.price }
          : item
      );
      setFormData({ ...formData, items: newItems });
    } else {
      const available = product.quantity - (product.reserved_quantity || 0);
      if (available <= 0) {
        toast.error(`${product.name} is out of stock`);
        return;
      }
      
      setFormData({
        ...formData,
        items: [...formData.items, {
          product_id: product.id,
          product_name: product.name,
          description: product.description || product.name,
          quantity: 1,
          price: product.price,
          total: product.price
        }]
      });
    }
    setProductSearch('');
    setFilteredProducts(products);
  };

  const calculateTotals = () => {
    let subtotal = formData.items.reduce((sum, item) => sum + item.total, 0);
    
    // Convert to selected currency if not USD
    if (formData.currency !== 'USD') {
      const rate = exchangeRates.find(r => r.currency === formData.currency);
      if (rate) {
        subtotal = subtotal * rate.rate_to_usd;
      }
    }
    
    const discountAmount = (subtotal * formData.discount) / 100;
    const taxAmount = ((subtotal - discountAmount) * formData.tax) / 100;
    const total = subtotal - discountAmount + taxAmount;
    return { subtotal, discountAmount, taxAmount, total };
  };

  const getCurrencySymbol = () => {
    if (formData.currency === 'USD') return '$';
    if (formData.currency === 'MVR') return 'ރ';
    if (formData.currency === 'CHN') return '¥';
    return formData.currency + ' ';
  };

  const removeItem = (productId) => {
    const newItems = formData.items.filter(item => item.product_id !== productId);
    setFormData({ ...formData, items: newItems });
  };

  const updateItemQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeItem(productId);
      return;
    }
    
    const product = products.find(p => p.id === productId);
    const available = product ? product.quantity - (product.reserved_quantity || 0) : 0;
    
    if (newQuantity > available) {
      toast.error(`Only ${available} units available`);
      return;
    }
    
    const newItems = formData.items.map(item =>
      item.product_id === productId
        ? { ...item, quantity: newQuantity, total: newQuantity * item.price }
        : item
    );
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.items.length === 0) {
      toast.error('Please add at least one product');
      return;
    }
    
    const { subtotal, discountAmount, taxAmount, total } = calculateTotals();

    try {
      await axios.post(`${API}/quotations`, {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        items: formData.items,
        subtotal,
        discount: discountAmount,
        tax: taxAmount,
        total,
        notes: formData.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Quotation created successfully! Stock reserved for 3 days.');
      setShowDialog(false);
      resetForm();
      fetchQuotations();
      fetchProducts(); // Refresh to show updated available quantities
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quotation');
    }
  };

  const convertToInvoice = async (quotation) => {
    try {
      await axios.post(`${API}/invoices`, {
        quotation_id: quotation.id,
        customer_name: quotation.customer_name,
        customer_email: quotation.customer_email,
        items: quotation.items,
        subtotal: quotation.subtotal,
        tax: quotation.tax,
        total: quotation.total,
        notes: quotation.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Converted to invoice successfully');
      fetchQuotations();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to invoice');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      items: [],
      discount: 0,
      tax: 10,
      notes: '',
      customer_notes: '',
      currency: 'USD'
    });
    setProductSearch('');
    setFilteredProducts(products);
  };

  const totals = calculateTotals();

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-slate-100 text-slate-700',
      sent: 'bg-blue-100 text-blue-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      expired: 'bg-orange-100 text-orange-700'
    };
    return colors[status] || colors.draft;
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    
    if (diff <= 0) return 'Expired';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h remaining`;
    return `${hours}h remaining`;
  };

  const handleExportPDF = () => {
    const rows = [];
    quotations.forEach((q) => {
      (q.items || []).forEach((item) => {
        rows.push([
          rows.length + 1,
          new Date(q.created_at).toLocaleDateString(),
          q.quotation_number || '-',
          item.product_name || '-',
          `$${fmtNum(item.price || 0)}`,
          item.quantity || 0,
          `$${fmtNum(item.total || 0)}`
        ]);
      });
    });
    const total = quotations.reduce((s, q) => s + (q.total || 0), 0);
    exportListPDF({
      title: 'Quotations',
      headers: ['#', 'Date', 'Quotation#', 'Item Name', 'Price', 'Qty', 'Total'],
      rows,
      totalLabel: 'Grand Total',
      totalValue: `$${fmtNum(total)}`,
      token, apiUrl: API,
    });
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="quotations-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Quotations</h1>
            <p className="text-slate-600 mt-1">{quotations.length} quotations created</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportPDF} data-testid="quotations-export-pdf">
              <FileDown className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-quotation-button">
                <Plus className="w-5 h-5 mr-2" />
                New Quotation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Quotation (Stock reserved for 3 days)</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {customers.length > 0 && (
                  <div>
                    <Label>Select Existing Customer (Optional)</Label>
                    <Select onValueChange={(value) => {
                      const customer = customers.find(c => c.id === value);
                      if (customer) selectCustomer(customer);
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose from saved customers..." />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name} {customer.email ? `(${customer.email})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Customer Name *</Label>
                    <Input
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Customer Email</Label>
                    <Input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Customer Phone</Label>
                    <Input
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    />
                  </div>
                </div>

                {/* Product Search */}
                <div>
                  <Label>Search & Add Products *</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      placeholder="Search products by name or SKU..."
                      value={productSearch}
                      onChange={(e) => searchProducts(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {productSearch && filteredProducts.length > 0 && (
                    <div className="mt-2 max-h-60 overflow-y-auto border rounded-lg">
                      {filteredProducts.map((product) => {
                        const available = product.quantity - (product.reserved_quantity || 0);
                        return (
                          <div
                            key={product.id}
                            onClick={() => available > 0 && addProductToQuotation(product)}
                            className={`p-3 border-b hover:bg-slate-50 ${available <= 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-medium text-slate-900">{product.name}</p>
                                <p className="text-sm text-slate-600">SKU: {product.sku} | ${fmtNum(product.price)}</p>
                              </div>
                              <div className="text-right">
                                <p className={`text-sm font-medium ${available > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                  {available > 0 ? `${available} available` : 'Out of stock'}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Selected Items */}
                <div>
                  <Label>Selected Products ({formData.items.length})</Label>
                  {formData.items.length === 0 ? (
                    <p className="text-sm text-slate-600 mt-2">No products added yet</p>
                  ) : (
                    <div className="space-y-2 mt-2">
                      {formData.items.map((item) => (
                        <div key={item.product_id} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{item.product_name}</p>
                              <p className="text-sm text-slate-600">{formatCurrency(item.price)} each</p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(item.product_id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => updateItemQuantity(item.product_id, item.quantity - 1)}
                                className="h-8 w-8 p-0"
                              >
                                -
                              </Button>
                              <span className="w-12 text-center font-medium">{item.quantity}</span>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => updateItemQuantity(item.product_id, item.quantity + 1)}
                                className="h-8 w-8 p-0"
                              >
                                +
                              </Button>
                            </div>
                            <p className="font-bold text-slate-900">{formatCurrency(item.total)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Currency *</Label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="USD">USD ($)</option>
                      {exchangeRates.map((rate) => (
                        <option key={rate.currency} value={rate.currency}>
                          {rate.currency} ({rate.currency === 'MVR' ? 'ރ' : rate.currency === 'CHN' ? '¥' : rate.currency})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Discount %</Label>
                    <Input
                      type="number"
                      value={formData.discount}
                      onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <Label>Tax %</Label>
                    <Input
                      type="number"
                      value={formData.tax}
                      onChange={(e) => setFormData({ ...formData, tax: parseFloat(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Internal Notes</Label>
                    <Input
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="For internal use only"
                    />
                  </div>
                </div>

                <div>
                  <Label>Special Notes for Customer</Label>
                  <textarea
                    className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-md"
                    value={formData.customer_notes}
                    onChange={(e) => setFormData({ ...formData, customer_notes: e.target.value })}
                    placeholder="These notes will be visible to the customer on the quotation"
                  />
                </div>

                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between text-slate-700">
                    <span>Subtotal:</span>
                    <span className="inline-flex items-center"><CurrencySymbol currency={formData.currency} />{fmtNum(totals.subtotal)}</span>
                  </div>
                  {formData.discount > 0 && (
                    <div className="flex justify-between text-slate-700">
                      <span>Discount ({formData.discount}%):</span>
                      <span className="inline-flex items-center text-red-600">-<CurrencySymbol currency={formData.currency} />{fmtNum(totals.discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-700">
                    <span>Tax ({formData.tax}%):</span>
                    <span className="inline-flex items-center">+<CurrencySymbol currency={formData.currency} />{fmtNum(totals.taxAmount)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t border-slate-200">
                    <span>Total:</span>
                    <span className="inline-flex items-center"><CurrencySymbol currency={formData.currency} />{fmtNum(totals.total)}</span>
                  </div>
                  {formData.currency !== 'USD' && (
                    <div className="text-xs text-slate-500 text-right pt-1">
                      Original: ${(totals.total / exchangeRates.find(r => r.currency === formData.currency)?.rate_to_usd || 1)} USD
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    Create Quotation (Reserve Stock)
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <SearchFilter
          data={quotations}
          onFilter={setFilteredQuotations}
          searchFields={['quotation_number', 'customer_name', 'customer_email']}
          placeholder="Search by quotation #, customer name..."
        />

        <div className="grid gap-4">
          {filteredQuotations.map((quotation) => (
            <Card key={quotation.id} className="p-6 hover:shadow-lg transition-all" data-testid={`quotation-${quotation.quotation_number}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <FileText className="w-5 h-5 text-slate-600" />
                    <h3 className="text-xl font-bold text-slate-900">{quotation.quotation_number}</h3>
                    <Badge className={getStatusColor(quotation.status)}>
                      {quotation.status}
                    </Badge>
                    {quotation.status !== 'expired' && quotation.status !== 'accepted' && quotation.expires_at && (
                      <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {getTimeRemaining(quotation.expires_at)}
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-700 font-medium">{quotation.customer_name}</p>
                  {quotation.customer_email && (
                    <p className="text-sm text-slate-600">{quotation.customer_email}</p>
                  )}
                  <p className="text-sm text-slate-500 mt-2">
                    Created: {new Date(quotation.created_at).toLocaleDateString()}
                  </p>
                  {quotation.expires_at && (
                    <p className="text-sm text-slate-500">
                      Expires: {new Date(quotation.expires_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-emerald-600">{formatCurrency(quotation.total)}</p>
                  <div className="flex flex-col gap-2 mt-3">
                    <Button
                      onClick={() => {
                        setViewingQuotation(quotation);
                        setShowViewer(true);
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full"
                      data-testid={`view-quotation-${quotation.quotation_number}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Button>
                    {quotation.status !== 'expired' && (
                      <Button
                        onClick={() => {
                          // Populate form with quotation data for editing
                          setFormData({
                            customer_name: quotation.customer_name,
                            customer_email: quotation.customer_email || '',
                            customer_phone: quotation.customer_phone || '',
                            items: quotation.items,
                            discount: (quotation.discount / quotation.subtotal * 100) || 0,
                            tax: (quotation.tax / (quotation.subtotal - quotation.discount) * 100) || 10,
                            notes: quotation.notes || ''
                          });
                          setShowDialog(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="w-full"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                    )}
                    {quotation.status !== 'accepted' && quotation.status !== 'expired' && (
                      <Button
                        onClick={() => convertToInvoice(quotation)}
                        className="bg-blue-600 hover:bg-blue-700 w-full"
                        size="sm"
                        data-testid={`convert-to-invoice-${quotation.quotation_number}`}
                      >
                        Convert to Invoice
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-2">Items:</p>
                <div className="space-y-1">
                  {quotation.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm text-slate-600">
                      <span>{item.product_name || item.description} (x{item.quantity})</span>
                      <span>{formatCurrency(item.total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Quotation Viewer */}
        <QuotationViewer
          quotation={viewingQuotation}
          open={showViewer}
          onClose={() => {
            setShowViewer(false);
            setViewingQuotation(null);
          }}
        />
      </div>
    </Layout>
  );
}